import React, { useState, useEffect } from 'react';
import logo from '../assets/wennze.png';

const SmartAppBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [appInstalled, setAppInstalled] = useState(false);
  const [isCheckingApp, setIsCheckingApp] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isVerySmall, setIsVerySmall] = useState(false);

  useEffect(() => {
    // Check if user is on mobile device and update responsively
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsVerySmall(window.innerWidth <= 480);
    };
    
    checkMobile(); // Initial check
    window.addEventListener('resize', checkMobile);
    
    // Check if user is on Android device
    const userAgent = navigator.userAgent.toLowerCase();
    const isAndroidDevice = /android/.test(userAgent);
    
    // Check if user has already dismissed the banner or chosen not to open app
    const hasDismissedBanner = localStorage.getItem('wennze_app_banner_dismissed');
    const hasDeclinedAppOpen = localStorage.getItem('wennze_app_open_declined');
    const dismissedTime = localStorage.getItem('wennze_app_banner_dismissed_time');
    
    // Reset dismissal after 7 days
    if (dismissedTime) {
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      if (parseInt(dismissedTime) < sevenDaysAgo) {
        localStorage.removeItem('wennze_app_banner_dismissed');
        localStorage.removeItem('wennze_app_banner_dismissed_time');
        localStorage.removeItem('wennze_app_open_declined');
      }
    }
    
    setIsAndroid(isAndroidDevice);
    
    if (isAndroidDevice && !hasDismissedBanner && !hasDeclinedAppOpen) {
      // Check if app is installed
      checkIfAppInstalled().then((installed) => {
        setAppInstalled(installed);
        setIsCheckingApp(false);
        setShowBanner(true);
      });
    } else {
      setIsCheckingApp(false);
    }
    
    // Cleanup function
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const checkIfAppInstalled = async () => {
    return new Promise((resolve) => {
      // Try to open the app using custom URL scheme - expo-router uses wennze://routename
      const appScheme = 'wennze://ProductsInfinite';
      const timeout = 2000; // 2 seconds timeout
      
      let timer = null;
      let hasFired = false;
      
      const cleanup = () => {
        if (timer) {
          clearTimeout(timer);
        }
        document.removeEventListener('visibilitychange', onVisibilityChange);
        window.removeEventListener('blur', onBlur);
      };
      
      const onVisibilityChange = () => {
        if (document.hidden) {
          // App likely opened
          if (!hasFired) {
            hasFired = true;
            cleanup();
            resolve(true);
          }
        }
      };
      
      const onBlur = () => {
        // Window lost focus, app might have opened
        if (!hasFired) {
          hasFired = true;
          cleanup();
          resolve(true);
        }
      };
      
      // Listen for page visibility changes and blur events
      document.addEventListener('visibilitychange', onVisibilityChange);
      window.addEventListener('blur', onBlur);
      
      // Set timeout to assume app is not installed
      timer = setTimeout(() => {
        if (!hasFired) {
          hasFired = true;
          cleanup();
          resolve(false);
        }
      }, timeout);
      
      // Try to open the app (won't work if not installed)
      try {
        window.location.href = appScheme;
      } catch (error) {
        if (!hasFired) {
          hasFired = true;
          cleanup();
          resolve(false);
        }
      }
    });
  };

  const handleOpenApp = () => {
    // Track app open attempt
    window.gtag?.('event', 'app_open_attempt', {
      source: 'smart_banner',
      platform: 'android'
    });
    
    // Try to open the app directly to the products infinite page
    // expo-router uses scheme://routename format (without /app/)
    const appDeepLink = 'wennze://ProductsInfinite';
    
    // Fallback to app root which redirects to ProductsInfinite
    const fallbackLink = 'wennze://';
    
    let redirected = false;
    const timeout = setTimeout(() => {
      if (!redirected) {
        // If app doesn't open, redirect to Play Store
        window.open('https://play.google.com/store/apps/details?id=com.wennze.app', '_blank');
      }
    }, 2000);
    
    const onVisibilityChange = () => {
      if (document.hidden) {
        redirected = true;
        clearTimeout(timeout);
        handleDismiss(); // Hide banner after successful app open
      }
    };
    
    document.addEventListener('visibilitychange', onVisibilityChange);
    
    // Try deep link first, then fallback
    try {
      window.location.href = appDeepLink;
      setTimeout(() => {
        if (!redirected) {
          window.location.href = fallbackLink;
        }
      }, 500);
    } catch (error) {
      window.location.href = fallbackLink;
    }
    
    // Cleanup listener after 3 seconds
    setTimeout(() => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    }, 3000);
  };

  const handleDeclineApp = () => {
    // User doesn't want to open app, remember their choice
    localStorage.setItem('wennze_app_open_declined', 'true');
    localStorage.setItem('wennze_app_banner_dismissed', 'true');
    localStorage.setItem('wennze_app_banner_dismissed_time', Date.now().toString());
    
    // Track decline
    window.gtag?.('event', 'app_open_declined', {
      source: 'smart_banner',
      platform: 'android'
    });
    
    setShowBanner(false);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('wennze_app_banner_dismissed', 'true');
    localStorage.setItem('wennze_app_banner_dismissed_time', Date.now().toString());
  };

  const handleDownload = () => {
    // Track download click (for users without app)
    window.gtag?.('event', 'app_download_click', {
      source: 'smart_banner',
      platform: 'android'
    });
    
    // Open Google Play Store
    window.open('https://play.google.com/store/apps/details?id=com.wennze.app', '_blank');
  };

  if (!isAndroid || !showBanner) {
    return null;
  }

  return (
    <>
      {/* Banner */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: '#003366', // Use consistent Wennze blue
        color: 'white',
        padding: isMobile ? '10px 12px' : '12px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        zIndex: 9999,
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        minHeight: isMobile ? '60px' : '72px'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '8px' : '12px', 
          flex: 1,
          minWidth: 0 // Allow text to shrink
        }}>
          {/* App Icon */}
          <div style={{
            width: isMobile ? 40 : 48,
            height: isMobile ? 40 : 48,
            backgroundColor: '#ffffff',
            borderRadius: isMobile ? 10 : 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            overflow: 'hidden',
            padding: '6px'
          }}>
            <img 
              src={logo}
              alt="Wennze" 
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain'
              }}
            />
          </div>
          
          {/* App Info */}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ 
              fontWeight: 'bold', 
              fontSize: isMobile ? '14px' : '16px',
              lineHeight: '1.2',
              marginBottom: '2px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {appInstalled ? 'Ouvrir l\'app Wennze' : 'Wennze'}
            </div>
            <div style={{ 
              fontSize: isMobile ? '11px' : '13px', 
              opacity: 0.9,
              lineHeight: '1.2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {isCheckingApp 
                ? 'Vérification...' 
                : appInstalled 
                  ? 'Continuez dans l\'application' 
                  : 'Expérience mobile optimisée'
              }
            </div>
          </div>
        </div>
        
        {!isCheckingApp && (
          <>
            {appInstalled ? (
              // App is installed - show "Open" and "No thanks" buttons
              <div style={{ 
                display: 'flex', 
                gap: isMobile ? '6px' : '8px',
                flexShrink: 0
              }}>
                <button
                  onClick={handleOpenApp}
                  style={{
                    backgroundColor: '#ffffff',
                    color: '#003366',
                    border: 'none',
                    padding: isMobile ? '8px 12px' : '10px 16px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                  onMouseOut={(e) => e.target.style.backgroundColor = '#ffffff'}
                >
                  Ouvrir
                </button>
                <button
                  onClick={handleDeclineApp}
                  style={{
                    backgroundColor: 'transparent',
                    color: 'white',
                    border: '1px solid rgba(255,255,255,0.3)',
                    padding: isMobile ? '8px 12px' : '10px 16px',
                    borderRadius: '8px',
                    fontWeight: 'bold',
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                    display: isVerySmall ? 'none' : 'block' // Hide on very small screens
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                  onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  {isMobile ? 'Non' : 'Non merci'}
                </button>
              </div>
            ) : (
              // App not installed - show download button
              <button
                onClick={handleDownload}
                style={{
                  backgroundColor: '#34A853',
                  color: 'white',
                  border: 'none',
                  padding: isMobile ? '8px 12px' : '10px 16px',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  fontSize: isMobile ? '12px' : '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginRight: isMobile ? '6px' : '8px',
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.2s ease',
                  flexShrink: 0
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#2d8f47'}
                onMouseOut={(e) => e.target.style.backgroundColor = '#34A853'}
              >
                <svg width={isMobile ? '14' : '16'} height={isMobile ? '14' : '16'} viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.61 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.92 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                {isMobile ? 'Installer' : 'Installer'}
              </button>
            )}
            
            {/* Close Button */}
            <button
              onClick={handleDismiss}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'white',
                fontSize: isMobile ? '18px' : '20px',
                cursor: 'pointer',
                padding: isMobile ? '6px' : '8px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? '28px' : '32px',
                height: isMobile ? '28px' : '32px',
                opacity: 0.8,
                transition: 'opacity 0.2s ease',
                flexShrink: 0
              }}
              onMouseOver={(e) => e.target.style.opacity = '1'}
              onMouseOut={(e) => e.target.style.opacity = '0.8'}
              title="Fermer"
            >
              ✕
            </button>
          </>
        )}
      </div>
      
      {/* Spacer to push content down */}
      <div style={{ height: isMobile ? '60px' : '72px' }} />
    </>
  );
};

export default SmartAppBanner;